import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { BehaviorSubject, defer, firstValueFrom, from, map, Observable, of, switchMap } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  BioBlend,
  BlendsResponse,
  CreateReportRequest,
  CreateReportResponse,
  CtmsDocumentPatchRequest,
  ReportFormInterface,
  Ship,
  ShipReportingRequirements,
  ShipSettings,
  ShipWithReportingRequirements,
  SubmittedReportsResponse,
  UpdateReportRequest,
  UpdateReportResponse,
} from 'src/types';
import { compareDate, formatReportForRequest } from 'src/utils';
import { v4 as uuidv4, validate as isUuid } from 'uuid';
import { mergeReports, hydrateBlendMetadataIntoReports } from 'src/utils/reports.utils';
import { setReportState } from '../store/reporting/reporting.actions';
import packageJson from '../../../../package.json';
import { IndexedDbService } from './indexed-db.service';
import { SKIP_ERROR_LOGGING } from '../interceptors/http-error.interceptor';
import { historyImportKey } from 'src/utils/history-import';

export interface ReportConflictResponse {
  state: 'available' | 'restricted' | 'stale';
  serverReport?: import('src/types').SubmittedReport;
  differingFields?: string[];
}

@Injectable({ providedIn: 'root' })
export class ReportingService {
  private readonly sourceVersion: string = packageJson.version;
  private writeQueue = Promise.resolve();
  private clearEpoch = 0;
  currentBioBlends = new BehaviorSubject<BioBlend[]>([]);

  constructor(
    private http: HttpClient,
    private store: Store,
    private idbService: IndexedDbService,
    private router: Router,
  ) { }

  getReports(imo: number): Observable<ReportFormInterface[]> {
    const epoch = this.clearEpoch, cleared = localStorage.getItem(`webform-cleared:${imo}`);
    return this.idbService.getItem<ReportFormInterface[]>(imo.toString()).pipe(
      switchMap((reports) => {
        if (reports != null) return of(reports);
        const raw = localStorage.getItem(imo.toString());
        if (!raw) return of(null);
        const migrated = JSON.parse(raw) as ReportFormInterface[];
        return this.idbService.updateItem<ReportFormInterface[]>(imo.toString(), current =>
          epoch === this.clearEpoch && cleared === localStorage.getItem(`webform-cleared:${imo}`) ? current ?? migrated : undefined).pipe(
          map((current) => {
            localStorage.removeItem(imo.toString());
            return current;
          }),
        );
      }),
      map(normalizeStoredReports),
      switchMap((reports) => this.getBioBlendsLocally(imo).pipe(
        map((bioBlends) => hydrateBlendMetadataIntoReports(reports, bioBlends))
      ))
    );
  }

  createReport(report: CreateReportRequest, uuid: string) {
    const reportWithVersion = { ...report, sourceVersion: this.sourceVersion };
    let url = `${environment.siglarApi.url}/api/v1/reports/${uuid}`;
    if (report.reportType === 'cosp') {
      url = `${environment.siglarApi.url}/api/v1/reports/${uuid}?bypassValidations=condition,speed`;
    }
    return this.http.post<CreateReportResponse>(url, reportWithVersion);
  }

  updateReport(report: UpdateReportRequest, uuid: string, id: string | number, version?: string) {
    const reportWithVersion = { ...report, sourceVersion: this.sourceVersion };
    let url = `${environment.siglarApi.url}/api/v1/reports/${uuid}/${id}`;
    if (report.reportType === 'cosp') {
      url = `${environment.siglarApi.url}/api/v1/reports/${uuid}/${id}?bypassValidations=condition,speed`;
    }
    return this.http.put<UpdateReportResponse>(url, reportWithVersion, version ? { headers: { 'If-Match': version } } : undefined);
  }

  updateCTMSDocument(ctmsDocumentUuid: string | null, uuid: string, id: string | number, version?: string) {
    const url = `${environment.siglarApi.url}/api/v1/reports/${uuid}/${id}`;
    const body: CtmsDocumentPatchRequest = { ctmsDocumentUuid };
    return this.http.patch<UpdateReportResponse>(url, body, version ? { headers: { 'If-Match': version } } : undefined);
  }

  initializeReports(imo: number) {
    return this.updateReports(imo, reports => reports.map(report =>
      report.localId ? report : { ...report, localId: uuidv4() })).pipe(switchMap(() => this.getReports(imo)));
  }

  setReportsLocally(reports: ReportFormInterface[], imo: number) {
    this.saveReportsLocallyAndWait(reports, imo).subscribe({
      error: (err) => console.error('Failed to save report to IndexedDB:', err),
    });
  }

  removeLastUsedUuid() {
    localStorage.removeItem('lastShipUuid');
  }

  removeShipReports(imo: number) {
    this.clearEpoch++;
    localStorage.removeItem(`${imo}-voyage-uuid`);
    localStorage.removeItem(imo.toString());
    return from(this.queueWrite(async () => {
      for (const key of [imo.toString(), `${imo}-voyage-uuid`, `${imo}-voyage-uuids`, 'store-snapshot']) {
        await firstValueFrom(this.idbService.removeItem(key));
      }
      this.store.dispatch(setReportState({ reports: [] }));
    }));
  }

  private queueWrite<T>(operation: () => Promise<T>): Promise<T> {
    const result = this.writeQueue.then(operation);
    this.writeQueue = result.then(() => undefined, () => undefined);
    return result;
  }

  private updateReports(imo: number, update: (reports: ReportFormInterface[]) => ReportFormInterface[]) {
    const epoch = this.clearEpoch;
    const cleared = localStorage.getItem(`webform-cleared:${imo}`);
    const active = () => epoch === this.clearEpoch && cleared === localStorage.getItem(`webform-cleared:${imo}`);
    return defer(() => from(this.queueWrite(async () => {
      if (!active()) return [];
      const reports = await firstValueFrom(this.idbService.updateItem<ReportFormInterface[]>(imo.toString(), current => {
        if (!active()) return undefined;
        const saved = current ?? JSON.parse(localStorage.getItem(imo.toString()) || '[]');
        return normalizeStoredReports(update(saved)).sort((a, b) => compareDate(new Date(a.reportedTime), new Date(b.reportedTime), true));
      })) ?? [];
      if (active()) this.store.dispatch(setReportState({ reports }));
      return reports;
    })));
  }

  mergeSavedReports(imo: number, incoming: import('src/types').SubmittedReport[]) {
    return firstValueFrom(this.updateReports(imo, current => {
      const saved = mergeReports(incoming, current.filter(r => incoming.some(s => String(s.id) === String(r.id)) && r.synced === true && !r.isSyncing));
      const byId = new Map(saved.map(r => [String(r.id), r]));
      const merged = current.map(local => {
        const source = byId.get(String(local.id));
        byId.delete(String(local.id));
        if (!source) return local;
        if (local.synced !== true || local.isSyncing)
          return source.issueAccessToken ? { ...local, issueAccessToken: source.issueAccessToken } : local;
        return { ...source, localId: local.localId, reportAccessToken: local.reportAccessToken,
          issueAccessToken: source.issueAccessToken || local.issueAccessToken };
      });
      return [...merged, ...byId.values()];
    }));
  }

  importConflictReport(imo: number, incoming: import('src/types').SubmittedReport) {
    return firstValueFrom(this.updateReports(imo, current => {
      const existing = current.find(report => report.synced === true && String(report.id) === String(incoming.id));
      const [formatted] = mergeReports([incoming], existing ? [existing] : []);
      const saved = existing ? {
        ...formatted, localId: existing.localId, reportAccessToken: existing.reportAccessToken,
        issueAccessToken: incoming.issueAccessToken || existing.issueAccessToken,
      } : formatted;
      return existing
        ? current.map(report => report.localId === existing.localId ? saved : report)
        : [...current, saved];
    }));
  }

  async inspectReportConflict(ship: Ship, draft: ReportFormInterface): Promise<ReportConflictResponse> {
    const [localReports, localBioBlends, settings, voyageUuids] = await Promise.all([
      firstValueFrom(this.getReports(ship.imo)),
      firstValueFrom(this.getBioBlendsLocally(ship.imo)),
      firstValueFrom(this.getShipSettingsLocally(ship.imo)),
      firstValueFrom(this.getVoyageUUIDs(ship.imo)),
    ]);
    const reportAccessTokens = localReports.flatMap(report => report.reportAccessToken ? [report.reportAccessToken] : []);
    const importToken = sessionStorage.getItem(historyImportKey(ship.uuid)) || undefined;
    const report = formatReportForRequest(draft, ship.imo, ship.ciiCfShipType, localReports,
      localBioBlends, settings, ship.shouldCollectLngN2Inputs);
    return firstValueFrom(this.http.post<ReportConflictResponse>(
      `${environment.siglarApi.url}/api/v1/webform/report-conflict`,
      { report, reportId: typeof draft.id === 'number' ? draft.id : undefined,
        voyageUuids, reportAccessTokens, ...(importToken ? { importToken } : {}) },
      { headers: { Authorization: `Vessel ${ship.uuid}` }, context: new HttpContext().set(SKIP_ERROR_LOGGING, true) }
    ));
  }

  discardLocalDraft(imo: number, localId: string) {
    return this.updateReports(imo, reports => reports.filter(report =>
      report.localId !== localId || report.synced === true));
  }

  removeShipSettings(imo: number) {
    this.idbService.removeItem(`${imo}-settings`).subscribe({
      error: (err) => console.error('Failed to remove ship settings from IndexedDB:', err),
    });
  }

  removeBlends(imo: number) {
    this.idbService.removeItem(`${imo}-bio-blends`).subscribe({
      error: (err) => console.error('Failed to remove bio-blends from IndexedDB:', err),
    });
    this.idbService.removeItem(`${imo}-synced-bio-blends`).subscribe({
      error: (err) => console.error('Failed to remove synced-bio-blends from IndexedDB:', err),
    });
  }

  saveReportLocallyAndWait(
    reports: ReportFormInterface[],
    report: ReportFormInterface,
    imo: number
  ): Observable<ReportFormInterface[]> {
    return this.updateReports(imo, current => {
      const existing = (current.length ? current : reports).filter(existingReport =>
        existingReport.localId === report.localId || !Number(report.id) ||
        String(existingReport.id) !== String(report.id) || !existingReport.synced || existingReport.isSyncing);
      return existing.some(r => r.localId === report.localId)
        ? existing.map(r => r.localId === report.localId ? report : r)
        : [...existing, report];
    });
  }

  saveReportsLocallyAndWait(reports: ReportFormInterface[], imo: number): Observable<ReportFormInterface[]> {
    return this.updateReports(imo, current => {
      const byLocalId = new Map(reports.map(report => [report.localId, report]));
      const updated = current.map(report => {
        const next = byLocalId.get(report.localId);
        byLocalId.delete(report.localId);
        // A background import or a newer draft may have arrived while documents were uploading.
        if (!next || report.syncRevision !== next.syncRevision || report.serverVersion !== next.serverVersion) return report;
        return next;
      });
      return [...updated, ...byLocalId.values()];
    });
  }

  acknowledgeReportSave(
    savedReport: ReportFormInterface,
    previousVersion: string | undefined,
    imo: number,
  ): Observable<ReportFormInterface[]> {
    return this.updateReports(imo, reports => reports.map(report => {
      // Read inside the IndexedDB transaction so an acknowledgement cannot replace
      // edits made while the request or document cleanup was in flight.
      if (!report.localId || report.localId !== savedReport.localId ||
          report.serverVersion !== previousVersion ||
          (report.id && report.id !== 'new' && String(report.id) !== String(savedReport.id))) {
        return report;
      }
      return {
        ...report,
        id: !report.id || report.id === 'new' ? savedReport.id : report.id,
        serverVersion: savedReport.serverVersion || report.serverVersion,
        reportAccessToken: report.reportAccessToken || savedReport.reportAccessToken,
        issueAccessToken: report.issueAccessToken || savedReport.issueAccessToken,
      };
    }));
  }

  saveShipLocally(ship: Ship) {
    this.idbService.setItem(ship.uuid, ship).subscribe({
      error: (err) => console.error('Failed to save ship to IndexedDB:', err),
    });
    localStorage.setItem('lastShipUuid', ship.uuid);
  }

  saveSelectedBunkersLocally(bunkers: string[]) {
    const bunks = JSON.stringify(bunkers);
    localStorage.setItem('bunkers', bunks);
  }

  getSelectedBunkers() {
    const bunkersFromLocalStorage = localStorage.getItem('bunkers') || '[]';
    const bunkers = JSON.parse(bunkersFromLocalStorage);
    return bunkers;
  }

  saveTimezoneLocally(timezone: string) {
    localStorage.setItem('timezone', timezone);
  }

  getShip(shipCode: string) {
    const shipUuidUrl = new URL('/api/v1/ship_uuids', environment.siglarApi.url);
    return this.http.get<{ data: ShipWithReportingRequirements[] }>(`${shipUuidUrl.href}/${shipCode}`);
  }

  getLastShipUuid() {
    const lastShipUuid = localStorage.getItem('lastShipUuid');
    return lastShipUuid;
  }

  getShipLocally(uuid: string) {
    return this.getCachedOrMigrate<Ship | null>(uuid, () => null);
  }

  getShipSettingsLocally(imo: number): Observable<ShipSettings> {
    return this.getCachedOrMigrate<ShipSettings>(`${imo}-settings`, () => ({} as ShipSettings));
  }

  saveShipSettingsLocally(imo: number, settings: ShipSettings) {
    this.idbService.setItem(`${imo}-settings`, settings).subscribe({
      error: (err) => console.error('Failed to save ship settings to IndexedDB:', err),
    });
  }

  getBioBlendsLocally(imo: number) {
    return this.getCachedOrMigrate<BioBlend[]>(`${imo}-bio-blends`, () => [], `${imo}-synced-bio-blends`);
  }

  private getCachedOrMigrate<T>(key: string, fallback: () => T, extraLegacyKey?: string): Observable<T> {
    return this.idbService.getItem<T>(key).pipe(
      switchMap((current) => {
        if (current != null) return of(current);
        const raw = localStorage.getItem(key);
        if (!raw) return of(fallback());
        const migrated = JSON.parse(raw) as T;
        return this.idbService.setItem(key, migrated).pipe(
          map(() => {
            localStorage.removeItem(key);
            if (extraLegacyKey) localStorage.removeItem(extraLegacyKey);
            return migrated;
          })
        );
      })
    );
  }

  saveBioBlendsLocally(bioBlends: BioBlend[], imo: number) {
    this.saveBioBlendsLocallyAndWait(bioBlends, imo).subscribe({
      error: (err) => console.error('Failed to save bio-blends to IndexedDB:', err),
    });
  }

  saveBioBlendsLocallyAndWait(bioBlends: BioBlend[], imo: number): Observable<void> {
    const reusableBioBlends = bioBlends.map((blend) => ({
      ...blend,
      components: (blend.components || []).map((component) => ({
        ...component,
        posDocumentRemovalId: null,
      })),
    }));
    const epoch = this.clearEpoch;
    return defer(() => from(this.queueWrite(async () => {
      if (epoch !== this.clearEpoch) return;
      this.currentBioBlends.next(reusableBioBlends);
      await firstValueFrom(this.idbService.setItem(`${imo}-bio-blends`, reusableBioBlends));
    }))).pipe(switchMap(() => epoch === this.clearEpoch
      ? this.updateReports(imo, reports => hydrateBlendMetadataIntoReports(reports, reusableBioBlends)).pipe(map(() => undefined))
      : of(undefined)));
  }

  getBlends(uuid: string) {
    const blendUuidUrl = new URL('/api/v1/blends', environment.siglarApi.url);
    return this.http.get<{ data: BlendsResponse[] }>(`${blendUuidUrl.href}/${uuid}`);
  }

  manageLocalReports(imo: number): Observable<void> {
    const url = new URL(location.href.toLowerCase());
    if (!['remove-by-id', 'remove-unsynced-by-id', 'remove-all-unsynced-reports'].some(key => url.searchParams.has(key))) return of(undefined);
    let removed = false;
    return this.updateReports(imo, reports => {
      let updatedReports = reports;
      if (url.searchParams.has('remove-by-id')) {
        const id = url.searchParams.get('remove-by-id');
        updatedReports = reports.filter((report) => String(report.id) !== id);
      } else if (url.searchParams.has('remove-unsynced-by-id')) {
        const id = url.searchParams.get('remove-unsynced-by-id');
        updatedReports = reports.filter((report) => !(String(report.id) === id && report.synced === false));
      } else if (url.searchParams.has('remove-all-unsynced-reports')) {
        updatedReports = reports.filter((report) => report.synced);
      }
      removed = reports.length !== updatedReports.length;
      return updatedReports;
    }).pipe(map(() => { if (removed) this.router.navigate(['/']); }));
  }

  getVoyageUUIDs(imo: number): Observable<string[]> {
    return this.idbService.getItem<string[]>(`${imo}-voyage-uuids`).pipe(
      switchMap(ids => this.idbService.getItem<string>(`${imo}-voyage-uuid`).pipe(
        map(legacy => [...new Set([...(ids || []), ...(legacy ? [legacy] : [])].filter(isUuid))])
      ))
    );
  }

  forgetVoyages(imo: number, unavailable: string[]) {
    return firstValueFrom(this.updateVoyageUUIDs(imo, ids => ids.filter(id => !unavailable.includes(id))));
  }

  getShipReportingRequirementsLocally(imo: number): Observable<ShipReportingRequirements | null> {
    return this.idbService.getItem<ShipReportingRequirements>(`ship-reporting-requirements:${imo}`);
  }

  saveShipReportingRequirementsLocally(imo: number, requirements: ShipReportingRequirements) {
    this.idbService.setItem(`ship-reporting-requirements:${imo}`, requirements).subscribe({
      error: (err) => console.error('Failed to save ship reporting requirements to IndexedDB:', err),
    });
  }

  removeShipReportingRequirementsLocally(imo: number) {
    this.idbService.removeItem(`ship-reporting-requirements:${imo}`).subscribe({
      error: (err) => console.error('Failed to remove ship reporting requirements from IndexedDB:', err),
    });
  }

  manageVoyageUUID(imo: number) {
    const url = new URL(location.href.toLowerCase());
    return this.updateVoyageUUIDs(imo, ids => url.searchParams.has('clear-voyage-uuid')
      ? [] : [...ids, ...url.searchParams.getAll('voyage-uuid')]);
  }

  private updateVoyageUUIDs(imo: number, update: (ids: string[]) => string[]) {
    const epoch = this.clearEpoch;
    return defer(() => from(this.queueWrite(async () => {
      if (epoch !== this.clearEpoch) return;
      const legacy = await firstValueFrom(this.idbService.getItem<string>(`${imo}-voyage-uuid`));
      if (epoch !== this.clearEpoch) return;
      await firstValueFrom(this.idbService.updateItem<string[]>(`${imo}-voyage-uuids`, current =>
        [...new Set(update([...(current || []), ...(legacy ? [legacy] : [])]).filter(isUuid))]));
      await firstValueFrom(this.idbService.removeItem(`${imo}-voyage-uuid`));
    })));
  }

  saveDraft(draft: unknown) {
    const epoch = this.clearEpoch;
    return defer(() => from(this.queueWrite(async () => {
      if (epoch === this.clearEpoch) await firstValueFrom(this.idbService.setItem('store-snapshot', draft));
    })));
  }

  loadSubmittedReports(voyageUUID: string) {
    const blendUuidUrl = new URL(`/api/v1/voyages`, environment.siglarApi.url);
    return this.http.get<SubmittedReportsResponse>(`${blendUuidUrl.href}/${voyageUUID}/reports`);
  }
}

const createReportedTime = (reportDate: string, time: string, timezone: string) => {
  const date = reportDate.split('T')[0];
  return `${date}T${time}:00${timezone}`;
};

function normalizeStoredReports(reports: ReportFormInterface[] | null): ReportFormInterface[] {
  return (reports || []).map(report => {
    let migrated = report;
    if ('sailingSpeedKt' in report) {
      const { sailingSpeedKt, ...rest } = report;
      migrated = { ...rest, speedOverGroundKT: sailingSpeedKt } as ReportFormInterface;
    }
    return { ...migrated, reportedTime: migrated.reportedTime ||
      createReportedTime(String(migrated.reportDate), migrated.localTime, migrated.timezone) };
  });
}
